/**
 * Validation schemas for authentication forms
 */

import { z } from 'zod';

/**
 * Username validation schema
 *
 * Alphanumeric characters, hyphens, and underscores. 2-50 characters.
 */
export const usernameSchema = z
  .string()
  .min(1, 'Username is required')
  .min(2, 'Username must be at least 2 characters')
  .max(50, 'Username must be 50 characters or less')
  .regex(/^[a-zA-Z0-9_-]+$/, 'Username can only contain letters, numbers, hyphens, and underscores');

/**
 * Email validation schema
 */
export const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Please enter a valid email address');

/**
 * Password validation schema
 *
 * Cognito requires min 8 characters.
 */
export const passwordSchema = z
  .string()
  .min(1, 'Password is required')
  .min(8, 'Password must be at least 8 characters');

/**
 * Login form validation schema
 *
 * Uses email as the sign-in identifier (Cognito alias).
 */
export const loginFormSchema = z.object({
  email: emailSchema,
  password: passwordSchema
});

/**
 * Registration form validation schema
 *
 * Includes password confirmation with cross-field validation.
 */
export const registerFormSchema = z.object({
  username: usernameSchema,
  email: emailSchema,
  password: passwordSchema,
  confirmPassword: z.string().min(1, 'Please confirm your password')
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword']
});

/**
 * Change password form validation schema
 *
 * Validates current password, new password, and confirmation.
 */
export const changePasswordFormSchema = z.object({
  currentPassword: passwordSchema,
  newPassword: passwordSchema,
  confirmNewPassword: z.string().min(1, 'Please confirm your new password')
}).refine((data) => data.newPassword !== data.currentPassword, {
  message: 'New password must be different from current password',
  path: ['newPassword']
}).refine((data) => data.newPassword === data.confirmNewPassword, {
  message: 'Passwords do not match',
  path: ['confirmNewPassword']
});

/**
 * TypeScript type inferred from the change password form schema
 */
export type ChangePasswordFormData = z.infer<typeof changePasswordFormSchema>;

/**
 * TypeScript type inferred from the login form schema
 */
export type LoginFormData = z.infer<typeof loginFormSchema>;

/**
 * TypeScript type inferred from the registration form schema
 */
export type RegisterFormData = z.infer<typeof registerFormSchema>;
